import {
  GlobalDesignPanelProps,
  GlobalDesignStyleGroup,
  GlobalDesignStyleItem,
  StylesGroup,
  DerivedActionStyle,
  ManifestProcessingStrategy,
} from '../../types';
import { DisplayGroups, EditorElement } from '@wix/component-protocol/schema';
import { CompRef } from '@wix/document-services-types';
import { EditorPointer } from '@wix/editorx-core-api';

import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import {
  ManifestRuntimeContextBuilder,
  ManifestContextAPI,
} from '@wix/editor-platform-manifest-services';

import {
  CssPropertyType,
  StyleItem,
  GroupType,
  DisplayGroupItemSelectedGroupTypeOneOf,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';

import actions from '../../actions';

const generateDefaultName = (
  actionName: StylesDerivedNativeActionNames,
): string => {
  return `${actionName} Group`;
};

const processDefaultGroups = (
  actionName: StylesDerivedNativeActionNames,
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: Record<
    string,
    GlobalDesignStyleItem<StylesDerivedNativeActionNames>
  >,
  predefinedGroups: StylesGroup<StylesDerivedNativeActionNames>[],
  context: ManifestContextAPI,
): StylesGroup<StylesDerivedNativeActionNames>[] => {
  const defaultGroup: StylesGroup<StylesDerivedNativeActionNames> = {
    displayName: generateDefaultName(actionName),
    editorPointer,
    items: [],
    context,
  };
  const styleItems = { ...allowedStyles };
  const styleItemsFromPredefinedGroups = new Set(
    predefinedGroups.flatMap((group) =>
      group.items.map((item) => item.styleItemId),
    ),
  );

  Object.entries(styleItems).forEach(([styleItemId, styleItem]) => {
    if (!styleItemsFromPredefinedGroups.has(styleItemId)) {
      defaultGroup.items.push({
        styleItemId,
        styleItem,
      });
    }
  });

  // TODO we might not need id with latest rfc, since now group can have duplicated properties. Consult with designers
  if (checkForDuplicatedProperties(defaultGroup)) {
    return [...splitGroupIntoSingleItemGroups(actionName, defaultGroup)];
  }
  return [defaultGroup];
};

const processDefinedGroups = (
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: Record<
    string,
    GlobalDesignStyleItem<StylesDerivedNativeActionNames>
  >,
  context: ManifestContextAPI,
): StylesGroup<StylesDerivedNativeActionNames>[] => {
  if (!allowedStyles) {
    return [];
  }

  const groups: DisplayGroups =
    context.resolver.getDisplayGroupDefinitions() || {};

  const mappedGroups = Object.entries(groups)
    .map(([_, group]) => {
      const groupType = group.groupType;
      if (!groupType || groupType === GroupType.UNKNOWN_GroupType) {
        return null;
      }
      let accessKey: keyof DisplayGroupItemSelectedGroupTypeOneOf;
      // TODO remove if inconsistency in manifest is fixed
      if (groupType === GroupType.arrayItem) {
        accessKey = 'listItem';
      } else {
        accessKey = groupType;
      }
      const itemsGroup = group[accessKey];

      if (
        !itemsGroup ||
        !('items' in itemsGroup) ||
        !Array.isArray(itemsGroup.items)
      ) {
        return null;
      }

      const groupStyleItems = itemsGroup.items
        .map((styleItemId: string) => {
          const styleItem = allowedStyles?.[styleItemId];

          if (!styleItem) {
            return null;
          }

          return {
            styleItemId,
            styleItem,
          };
        })
        .filter((groupStyleItem: any) => groupStyleItem !== null);

      return {
        displayName: group.displayName,
        editorPointer,
        items: groupStyleItems,
        context,
      };
    })
    .filter(
      (group): group is StylesGroup<StylesDerivedNativeActionNames> =>
        group !== null && group.items.length > 0,
    );

  return mappedGroups;
};

const checkForDuplicatedProperties = (
  styleGroup: StylesGroup<StylesDerivedNativeActionNames>,
) => {
  const properties = styleGroup.items.map(
    (item) => item.styleItem.cssPropertyType,
  );
  const uniqueProperties = [...new Set(properties)];
  return uniqueProperties.length !== properties.length;
};

const splitGroupIntoSingleItemGroups = (
  actionName: StylesDerivedNativeActionNames,
  styleGroup: StylesGroup<StylesDerivedNativeActionNames>,
): StylesGroup<StylesDerivedNativeActionNames>[] => {
  return styleGroup.items.map((item) => ({
    displayName: generateDefaultName(actionName),
    editorPointer: styleGroup.editorPointer,
    items: [item],
    context: styleGroup.context,
  }));
};

const applyGrouping = (
  actionName: StylesDerivedNativeActionNames,
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: DerivedActionStyle,
  context: ManifestContextAPI,
): StylesGroup<StylesDerivedNativeActionNames>[] => {
  const predefinedGroups = processDefinedGroups(
    editorPointer,
    allowedStyles,
    context,
  );
  const defaultGroups = processDefaultGroups(
    actionName,
    editorPointer,
    allowedStyles,
    predefinedGroups,
    context,
  );

  return [...defaultGroups, ...predefinedGroups];
};

const filterStylesByActionName = (
  actionName: StylesDerivedNativeActionNames,
  styleProperties: DerivedActionStyle | Record<string, StyleItem>,
): DerivedActionStyle => {
  const allowedStyles: readonly CssPropertyType[] =
    ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME[actionName];

  if (!allowedStyles) {
    return {};
  }

  const filteredStyles: DerivedActionStyle = {};

  const isAllowedStyle = (
    value: StyleItem,
  ): value is GlobalDesignStyleItem<StylesDerivedNativeActionNames> => {
    const cssPropertyType = value?.cssPropertyType;

    return !!cssPropertyType && allowedStyles.includes(cssPropertyType);
  };

  Object.keys(styleProperties).forEach((key) => {
    const value = styleProperties[key];
    if (isAllowedStyle(value)) {
      filteredStyles[key] = value;
    }
  });

  return filteredStyles;
};

const processElement = async (
  actionName: StylesDerivedNativeActionNames,
  editorPointer: EditorPointer<CompRef>,
  builder: ManifestRuntimeContextBuilder,
  context: ManifestContextAPI,
  manifestProcessingStrategy: ManifestProcessingStrategy,
  groups: StylesGroup<StylesDerivedNativeActionNames>[],
) => {
  const styles = context.resolver.getStyleDefinitions() || {};

  const valueFilter = actions[actionName].valueFilter;

  let allowedStyles = filterStylesByActionName(actionName, styles);

  if (valueFilter) {
    allowedStyles = await valueFilter(
      allowedStyles,
      context,
      manifestProcessingStrategy,
    );
  }

  if (Object.keys(allowedStyles).length > 0) {
    groups.push(
      ...applyGrouping(actionName, editorPointer, allowedStyles, context),
    );
  }
  const childrenContexts = context.getChildren();

  if (
    childrenContexts &&
    manifestProcessingStrategy === ManifestProcessingStrategy.Recursive
  ) {
    for (const childContext of Object.values(childrenContexts)) {
      const childPointer: EditorPointer<CompRef> | null =
        childContext.getEditorPointer();

      if (!childPointer) {
        return;
      }

      await processElement(
        actionName,
        childPointer,
        builder,
        childContext,
        manifestProcessingStrategy,
        groups,
      );
    }
  }
};

const createHost = async (
  editorPointer: EditorPointer<CompRef>,
  styleGroups: StylesGroup<StylesDerivedNativeActionNames>['items'],
  getHost: GlobalDesignPanelProps<StylesDerivedNativeActionNames>['getHost'],
) => {
  const host = await getHost({
    contextBuilder: (builder) => {
      const element: EditorElement = {
        style: {},
      };

      styleGroups.forEach((groupItem) => {
        element.style![groupItem.styleItemId] = groupItem.styleItem;
      });
      builder.withEditorElement(editorPointer, element);

      // styleGroups.forEach((groupItem) => {
      //   if (
      //     groupItem.linkedStyleItems &&
      //     groupItem.linkedStyleItems.length > 0
      //   ) {
      //     builder.withLinkedStyles(
      //       {
      //         editorPointer,
      //         key: groupItem.styleItemId,
      //       },
      //       groupItem.linkedStyleItems.map((linkedStyleItem) => ({
      //         editorPointer: linkedStyleItem.editorPointer,
      //         key: linkedStyleItem.styleItemId,
      //       })),
      //     );
      //   }
      // });
    },
  });

  return host;
};

export const groupByGroupDefinitions = async <
  T extends StylesDerivedNativeActionNames,
>(
  editorPointer: EditorPointer<CompRef>,
  actionName: T,
  getHost: GlobalDesignPanelProps<T>['getHost'],
): Promise<GlobalDesignStyleGroup[]> => {
  let groups: StylesGroup<T>[] = [];
  let contextProcessingPromise: (() => Promise<void>) | undefined;

  await getHost({
    contextBuilder: (builder: ManifestRuntimeContextBuilder) => {
      builder.byEditorPointer(editorPointer, (_, context) => {
        contextProcessingPromise = async () => {
          const manifestProcessingStrategy =
            (await actions[actionName].getManifestProcessingStrategy?.(
              context,
            )) || ManifestProcessingStrategy.Recursive;

          await processElement(
            actionName,
            editorPointer,
            builder,
            context,
            manifestProcessingStrategy,
            groups,
          );

          const applyValueMergingStrategy =
            actions[actionName].grouping.applyValueMergingStrategy;

          if (applyValueMergingStrategy) {
            const mergedGroups = await applyValueMergingStrategy(groups);
            groups = [...mergedGroups];
          }
        };
      });
    },
  });

  if (!contextProcessingPromise) {
    return [];
  }

  await contextProcessingPromise();

  const hostCreationPromises: Promise<GlobalDesignStyleGroup>[] = [];

  for (const group of groups) {
    hostCreationPromises.push(
      createHost(group.editorPointer, group.items, getHost).then((host) => ({
        host,
        displayName: group.displayName,
      })),
    );
  }
  return Promise.all(hostCreationPromises);
};
